/**
 * The chat app's URL contract — SPEC-2026-09-internal-chat-pwa (T-25 · RF-02 · RF-11).
 *
 * One address per state the chat can be in, so a reload, a bookmark, a push
 * notification and the product's "open in the chat app" all land in the same place:
 *
 * | Path                              | State                                   |
 * |-----------------------------------|-----------------------------------------|
 * | `/`                               | the conversation list                   |
 * | `/c/:channelId`                   | a conversation                          |
 * | `/c/:channelId/m/:messageId`      | a conversation, revealing a message     |
 * | `/c/:channelId/t/:threadRootId`   | a conversation with a thread open       |
 * | `/dm/:userId`                     | the direct conversation with a person   |
 * | `/saved` · `/threads` · `/team`   | a pane                                  |
 * | `/search?q=term`                  | the search pane, optionally pre-typed   |
 *
 * The backend builds the conversation and message forms for push links
 * (`AppsURLsUtil.buildChatConversationUrl`); this file is the client-side source of
 * the whole shape. Ids are always percent-encoded.
 */

/** Panes that have an address of their own. */
export type ChatAppView = 'saved' | 'threads' | 'team' | 'search';

/** A destination inside the chat app. At most one of the primary fields is used. */
export interface ChatAppTarget {
  channelId?: string;
  /** Message to reveal inside `channelId`. */
  messageId?: string;
  /** Thread to open inside `channelId` (the root message id). */
  threadRootId?: string;
  /** Person to open the direct conversation with. */
  directUserId?: string;
  view?: ChatAppView;
  /** Pre-typed term of the search pane. */
  searchTerm?: string;
}

export const CHAT_APP_VIEWS: ReadonlyArray<ChatAppView> = ['saved', 'threads', 'team', 'search'];

/** First path segments the contract knows. Anything else is not a chat app destination. */
const CHAT_APP_FIRST_SEGMENTS: ReadonlySet<string> = new Set(['c', 'dm', ...CHAT_APP_VIEWS]);

/** Query parameter carrying the search term. */
export const CHAT_APP_SEARCH_PARAM = 'q';

const isChatAppView = (value: string): value is ChatAppView =>
  (CHAT_APP_VIEWS as ReadonlyArray<string>).includes(value);

const clean = (value: string | null | undefined): string =>
  typeof value === 'string' ? value.trim() : '';

const encode = (value: string): string => encodeURIComponent(value);

const decode = (value: string): string | null => {
  try {
    const decoded = decodeURIComponent(value);
    return decoded.trim() ? decoded : null;
  } catch {
    return null;
  }
};

/**
 * Path of a destination. Precedence when several fields are set: conversation (with
 * thread over message), then direct conversation, then pane; an empty target is `/`.
 */
export function buildChatAppPath(target: ChatAppTarget | null | undefined): string {
  const channelId = clean(target?.channelId);
  if (channelId) {
    const base = `/c/${encode(channelId)}`;
    const threadRootId = clean(target?.threadRootId);
    if (threadRootId) return `${base}/t/${encode(threadRootId)}`;
    const messageId = clean(target?.messageId);
    if (messageId) return `${base}/m/${encode(messageId)}`;
    return base;
  }

  const directUserId = clean(target?.directUserId);
  if (directUserId) return `/dm/${encode(directUserId)}`;

  const view = target?.view;
  if (view && isChatAppView(view)) {
    const term = clean(target?.searchTerm);
    return view === 'search' && term
      ? `/search?${CHAT_APP_SEARCH_PARAM}=${encode(term)}`
      : `/${view}`;
  }

  return '/';
}

/**
 * Destination of a path, or `null` when the path is not a chat app destination
 * (unsafe, unknown first segment, missing or extra segments). `/` parses to `{}`.
 */
export function parseChatAppPath(path: string | null | undefined): ChatAppTarget | null {
  if (!isSafeChatAppPath(path)) return null;

  const [pathname, rawQuery = ''] = splitQuery(path);
  const segments = pathname.split('/').filter((segment) => segment.length > 0);
  if (segments.length === 0) return {};

  const [first, ...rest] = segments;

  if (first === 'c') {
    const channelId = rest[0] ? decode(rest[0]) : null;
    if (!channelId) return null;
    if (rest.length === 1) return { channelId };
    if (rest.length !== 3) return null;
    const id = decode(rest[2]);
    if (!id) return null;
    if (rest[1] === 'm') return { channelId, messageId: id };
    if (rest[1] === 't') return { channelId, threadRootId: id };
    return null;
  }

  if (first === 'dm') {
    if (rest.length !== 1) return null;
    const directUserId = decode(rest[0]);
    return directUserId ? { directUserId } : null;
  }

  if (isChatAppView(first) && rest.length === 0) {
    if (first !== 'search') return { view: first };
    const searchTerm = readQueryParam(rawQuery, CHAT_APP_SEARCH_PARAM);
    return searchTerm ? { view: first, searchTerm } : { view: first };
  }

  return null;
}

/**
 * Whether a value may be used as an in-app return path (sign-in `returnUrl`, handoff
 * destination, launcher target). Only a same-origin path of the contract is safe:
 * absolute URLs, protocol-relative paths (`//host`), backslash tricks, control
 * characters and unknown first segments are refused, so a crafted link can never
 * send the user to another origin or to a page outside the contract.
 */
export function isSafeChatAppPath(path: unknown): path is string {
  if (typeof path !== 'string' || path.length === 0 || path.length > 2048) return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  // Browsers normalise `\` to `/`, which turns `/\host` into a protocol-relative URL.
  if (path.includes('\\')) return false;
  // Control characters (tab, newline…) are stripped by URL parsers before resolution.
  if (/[\u0000-\u001F\u007F]/.test(path)) return false;
  if (path.includes('#')) return false;

  const [pathname] = splitQuery(path);
  const first = pathname.split('/').filter((segment) => segment.length > 0)[0];
  if (first === undefined) return pathname === '/';
  return CHAT_APP_FIRST_SEGMENTS.has(first);
}

function splitQuery(path: string): [string, string] {
  const index = path.indexOf('?');
  return index === -1 ? [path, ''] : [path.slice(0, index), path.slice(index + 1)];
}

function readQueryParam(query: string, name: string): string | null {
  for (const pair of query.split('&')) {
    const [key, value = ''] = pair.split('=');
    if (key === name) {
      return decode(value.replace(/\+/g, ' '));
    }
  }
  return null;
}
